/**
 * UI kit — the Phase 0 pattern set pieces FE-1 uses. Every interactive
 * element meets the 44px floor; helper text is permanent; disabled states
 * always travel with a reason in words next to them.
 */

import { useId, useRef, useState } from 'react';
import s from './ui.module.css';

/* ---------------------------------- Button --------------------------------- */

type ButtonVariant = 'primary' | 'brand' | 'quiet' | 'danger' | 'link';
const variantClass: Record<ButtonVariant, string> = {
  primary: s.btnPrimary!,
  brand: s.btnBrand!,
  quiet: s.btnQuiet!,
  danger: s.btnDanger!,
  link: s.btnLink!,
};

export function Button(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ButtonVariant;
    busy?: boolean;
  },
) {
  const { variant = 'quiet', busy, className, children, ...rest } = props;
  return (
    <button
      // A plain action unless it says otherwise: an HTML button inside a form
      // defaults to SUBMIT, so a day chip or a "Choose" inside the booking
      // form used to submit it — and book a session nobody asked for
      // (2026-10-18). A form's own submit passes type="submit" explicitly.
      type="button"
      {...rest}
      className={[s.btn, variantClass[variant], busy ? s.btnBusy : '', className ?? ''].join(' ')}
      aria-busy={busy || undefined}
    >
      {children}
    </button>
  );
}

/* ---------------------------------- Bdi ------------------------------------ */

/** Machine-facing island: Latin, LTR, tabular — phones, codes, emails. */
export function Machine({ children }: { children: React.ReactNode }) {
  return (
    <bdi className="zc-machine" dir="ltr">
      {children}
    </bdi>
  );
}

/* ---------------------------------- Field ----------------------------------- */

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
  /** 'required' | 'optional' word next to the label — never an asterisk. */
  requirement?: { word: string; kind: 'required' | 'optional' };
  error?: string;
  ok?: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url';
  /** Machine-facing values (emails, phones) render LTR in both languages. */
  machine?: boolean;
  prefix?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoFocus?: boolean;
  name?: string;
  /** Shown for context, not editable here (e.g. a Guest's name during registration). */
  readOnly?: boolean;
  /** Commit-on-leave fields (a follow-up's next step) save here. */
  onBlur?: () => void;
}

export function TextField(props: TextFieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const hasError = Boolean(props.error);
  return (
    <label className={s.field} htmlFor={id}>
      <span className={s.fieldTop}>
        <span className={s.fieldLabel}>{props.label}</span>
        {props.requirement && (
          <span
            className={[
              s.fieldReq,
              props.requirement.kind === 'required' ? s.fieldReqRequired : s.fieldReqOptional,
            ].join(' ')}
          >
            {props.requirement.word}
          </span>
        )}
      </span>
      <span className={[s.fieldBox, hasError ? s.fieldBoxError : ''].join(' ')}>
        {props.prefix && (
          <span className={s.fieldPrefix} dir="ltr">
            {props.prefix}
          </span>
        )}
        <input
          id={id}
          name={props.name}
          className={s.fieldInput}
          type={props.type ?? 'text'}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          aria-invalid={hasError || undefined}
          aria-describedby={helpId}
          autoComplete={props.autoComplete}
          inputMode={props.inputMode}
          dir={props.machine ? 'ltr' : undefined}
          autoFocus={props.autoFocus}
          readOnly={props.readOnly}
          onBlur={props.onBlur}
        />
      </span>
      <span
        id={helpId}
        className={[
          s.fieldHelp,
          hasError ? s.fieldHelpError : '',
          !hasError && props.ok ? s.fieldHelpOk : '',
        ].join(' ')}
      >
        {props.error ?? props.ok ?? props.help ?? ' '}
      </span>
    </label>
  );
}

export function PasswordField(
  props: Omit<TextFieldProps, 'type' | 'machine'> & { showWord: string; hideWord: string },
) {
  const [visible, setVisible] = useState(false);
  const id = useId();
  const helpId = `${id}-help`;
  const hasError = Boolean(props.error);
  return (
    <label className={s.field} htmlFor={id}>
      <span className={s.fieldTop}>
        <span className={s.fieldLabel}>{props.label}</span>
        {props.requirement && (
          <span
            className={[
              s.fieldReq,
              props.requirement.kind === 'required' ? s.fieldReqRequired : s.fieldReqOptional,
            ].join(' ')}
          >
            {props.requirement.word}
          </span>
        )}
      </span>
      <span className={[s.fieldBox, hasError ? s.fieldBoxError : ''].join(' ')}>
        <input
          id={id}
          className={s.fieldInput}
          type={visible ? 'text' : 'password'}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          aria-invalid={hasError || undefined}
          aria-describedby={helpId}
          autoComplete={props.autoComplete ?? 'current-password'}
          dir="ltr"
        />
        <button
          type="button"
          className={s.eyeBtn}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? props.hideWord : props.showWord}
          aria-pressed={visible}
        >
          <EyeIcon off={visible} />
        </button>
      </span>
      <span
        id={helpId}
        className={[
          s.fieldHelp,
          hasError ? s.fieldHelpError : '',
          !hasError && props.ok ? s.fieldHelpOk : '',
        ].join(' ')}
      >
        {props.error ?? props.ok ?? props.help ?? ' '}
      </span>
    </label>
  );
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <path d="M12 14.4A2.4 2.4 0 1 0 12 9.6a2.4 2.4 0 0 0 0 4.8z" />
      {off && <path d="M4 20 20 4" />}
    </svg>
  );
}

/* ------------------------------- Segmented pill ----------------------------- */

export function Segmented<T extends string>(props: {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className={s.segment} role="group" aria-label={props.label}>
      {props.options.map((o) => {
        const on = o.value === props.value;
        return (
          <button
            key={o.value}
            type="button"
            className={[s.segmentOpt, on ? s.segmentOptOn : ''].join(' ')}
            aria-pressed={on}
            onClick={() => props.onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------- Notice ---------------------------------- */

export type NoticeKind = 'info' | 'ok' | 'warn' | 'err';
const noticeClass: Record<NoticeKind, string> = {
  info: s.noticeInfo!,
  ok: s.noticeOk!,
  warn: s.noticeWarn!,
  err: s.noticeErr!,
};

const noticePaths: Record<NoticeKind, string> = {
  info: 'M12 21a9 9 0 1 0-9-9|M12 8h.01|M11.4 12h.9v4.2h.9',
  ok: 'M5 13l4 4L19 7',
  warn: 'M12 4l9 16H3z|M12 10v4|M12 17h.01',
  err: 'M12 4l9 16H3z|M12 10v4|M12 17h.01',
};

export function Notice(props: {
  kind: NoticeKind;
  title?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  role?: 'alert' | 'status';
}) {
  return (
    <div className={[s.notice, noticeClass[props.kind]].join(' ')} role={props.role ?? 'status'}>
      <svg
        className={s.noticeIcon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {noticePaths[props.kind].split('|').map((d) => (
          <path key={d} d={d} />
        ))}
      </svg>
      <div>
        {props.title && <div className={s.noticeTitle}>{props.title}</div>}
        {props.children}
        {props.action && <div className={s.noticeAction}>{props.action}</div>}
      </div>
    </div>
  );
}

/* ---------------------------------- Chip ------------------------------------ */

export function Chip(props: { kind: NoticeKind | 'quiet'; children: React.ReactNode }) {
  const palette: Record<string, { bg: string; border: string; ink: string; dot: string }> = {
    info: {
      bg: 'var(--zc-info-bg)',
      border: 'var(--zc-info-border)',
      ink: 'var(--zc-info-ink)',
      dot: 'var(--zc-info-dot)',
    },
    ok: {
      bg: 'var(--zc-sage-bg)',
      border: 'var(--zc-sage-border)',
      ink: 'var(--zc-sage-ink)',
      dot: 'var(--zc-sage-dot)',
    },
    warn: {
      bg: 'var(--zc-warn-bg)',
      border: 'var(--zc-warn-border)',
      ink: 'var(--zc-warn-ink)',
      dot: 'var(--zc-warn-dot)',
    },
    err: {
      bg: 'var(--zc-err-bg)',
      border: 'var(--zc-err-border)',
      ink: 'var(--zc-err-ink)',
      dot: 'var(--zc-err-dot)',
    },
    quiet: {
      bg: 'var(--zc-warm)',
      border: 'var(--zc-line)',
      ink: 'var(--zc-muted)',
      dot: 'var(--zc-faint)',
    },
  };
  const p = palette[props.kind]!;
  return (
    <span className={s.chip} style={{ background: p.bg, borderColor: p.border, color: p.ink }}>
      <span className={s.chipDot} style={{ background: p.dot }} />
      {props.children}
    </span>
  );
}

/* --------------------------------- OTP input --------------------------------- */

export function OtpInput(props: {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (code: string) => void;
  error?: boolean;
  label: string;
  describedBy?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const digits = props.value.padEnd(6).slice(0, 6).split('');
  const active = Math.min(props.value.length, 5);
  return (
    <div className={s.otpWrap} onClick={() => inputRef.current?.focus()}>
      {digits.map((d, i) => (
        <span
          key={i}
          className={[
            s.otpCell,
            props.error ? s.otpCellError : '',
            !props.error && i === active ? s.otpCellActive : '',
          ].join(' ')}
          aria-hidden
        >
          {d.trim()}
        </span>
      ))}
      <input
        ref={inputRef}
        className={s.otpInput}
        value={props.value}
        onChange={(e) => {
          const v = e.currentTarget.value.replace(/\D/g, '').slice(0, 6);
          props.onChange(v);
          if (v.length === 6) props.onComplete?.(v);
        }}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        aria-label={props.label}
        aria-invalid={props.error || undefined}
        aria-describedby={props.describedBy}
      />
    </div>
  );
}

/* --------------------------------- Skeleton ---------------------------------- */

export function Skeleton(props: { width?: string; height?: string; style?: React.CSSProperties }) {
  return (
    <span
      className={s.skeleton}
      style={{
        display: 'inline-block',
        inlineSize: props.width ?? '100%',
        blockSize: props.height ?? '14px',
        ...props.style,
      }}
      aria-hidden
    />
  );
}

export function Card(props: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <div className={[s.card, props.className ?? ''].join(' ')} style={props.style}>
      {props.children}
    </div>
  );
}

/* ------------------------- Password rules panel (slots) ----------------------- */

/**
 * ZOY-632: the rules the Auth service really enforces, one row each, with a
 * live state as the person types — met, not yet met, or pending (nothing
 * typed, or a rule only the server can judge). No rule is invented here;
 * the caller passes exactly what Auth validates.
 */
export function PasswordRulesPanel(props: {
  title: string;
  rules: readonly { id: string; label: string; state: 'pending' | 'met' | 'unmet' }[];
  /** Screen-reader words for the three states. */
  stateWords: { pending: string; met: string; unmet: string };
  note: string;
}) {
  return (
    <div className={s.rules}>
      <div className={s.rulesTitle}>{props.title}</div>
      <ul className={s.ruleList}>
        {props.rules.map((r) => (
          <li
            key={r.id}
            className={[
              s.ruleRow,
              r.state === 'met' ? s.ruleRowMet : r.state === 'unmet' ? s.ruleRowUnmet : '',
            ].join(' ')}
          >
            <span
              className={[
                s.ruleDot,
                r.state === 'met' ? s.ruleDotMet : r.state === 'unmet' ? s.ruleDotUnmet : '',
              ].join(' ')}
              aria-hidden="true"
            >
              {r.state === 'met' ? '✓' : r.state === 'unmet' ? '•' : ''}
            </span>
            <span>{r.label}</span>
            <span className={s.srOnly}>{props.stateWords[r.state]}</span>
          </li>
        ))}
      </ul>
      <div className={s.rulesNote}>{props.note}</div>
    </div>
  );
}
